import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, Clock, CreditCard, Activity, Inbox, LifeBuoy, ArrowRight, Calendar, Lock } from "lucide-react";
import api, { GHS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, PredictionPill } from "@/components/StatusBadge";
import { useCountdown } from "@/hooks/useCountdown";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  const sub = data?.subscription;
  const countdown = useCountdown(sub?.has_subscription && sub?.type === "time_pass" && sub?.status === "ACTIVE" ? sub?.expires_at : null);

  if (loading) return <Skeleton />;

  const active = sub?.has_subscription && sub?.status === "ACTIVE";
  const isTime = sub?.type === "time_pass";
  const creditsLeft = sub?.credits_remaining;

  return (
    <div className="space-y-7" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="vw-label">Dashboard</p>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-white mt-1">
            Welcome back, <span style={{ color: "#00FF66" }}>{user?.phone}</span>
          </h1>
        </div>
        <StatusBadge status={user?.status} testid="dashboard-account-status" />
      </div>

      {/* main CTA */}
      {active ? (
        <div className="vw-glow-card p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
          <div>
            <h2 className="font-display text-xl font-bold text-white">VirtualWin Predictor</h2>
            <p className="mt-1 text-slate-400 text-sm">Upload a fixture screenshot and get your strongest selections.</p>
          </div>
          <button onClick={() => navigate("/predictor")} className="vw-btn-primary inline-flex items-center justify-center gap-2 whitespace-nowrap" data-testid="dashboard-open-predictor">
            <Zap size={18} /> OPEN VIRTUALWIN PREDICTOR
          </button>
        </div>
      ) : (
        <div className="vw-card p-6 sm:p-8" data-testid="dashboard-predictor-locked">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 shrink-0 grid place-items-center rounded-xl bg-amber-500/15 text-amber-400"><Lock size={22} /></div>
              <div>
                <h2 className="font-display text-xl font-bold text-white">
                  {sub?.latest_payment_status === "PENDING" ? "PAYMENT UNDER REVIEW"
                    : sub?.latest_payment_status === "REJECTED" ? "PAYMENT REJECTED"
                    : sub?.has_subscription ? "SUBSCRIPTION EXPIRED" : "PREDICTOR LOCKED"}
                </h2>
                <p className="mt-1 text-slate-400 text-sm">
                  {sub?.latest_payment_status === "PENDING" ? "Your payment is awaiting admin approval. The predictor unlocks once it's approved."
                    : sub?.latest_payment_status === "REJECTED" ? "Your payment could not be approved. Please submit a new payment proof."
                    : "Subscribe to a plan and get your payment approved by admin to start analyzing fixtures."}
                </p>
              </div>
            </div>
            <button onClick={() => navigate(sub?.latest_payment_status === "PENDING" ? "/payment-history" : "/plans")} className="vw-btn-primary inline-flex items-center justify-center gap-2 whitespace-nowrap" data-testid="dashboard-locked-cta">
              {sub?.latest_payment_status === "PENDING" ? "VIEW PAYMENT STATUS" : sub?.latest_payment_status === "REJECTED" ? "SUBMIT NEW PAYMENT" : "VIEW PLANS"}
            </button>
          </div>
          <div className="mt-6 rounded-2xl overflow-hidden border border-slate-800" data-testid="dashboard-locked-image">
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_virtualwin-preview/artifacts/vbydvwm2_photo_2026-10-02_13-46-30.jpg"
              alt="SportyBet Instant Virtuals"
              loading="lazy"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      )}

      {/* subscription cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={CreditCard} label="Current Plan" value={sub?.has_subscription ? sub.plan_name : "No plan"}
          sub={sub?.has_subscription ? GHS(sub.amount || 0) : "—"} testid="stat-plan" />
        <Stat icon={Activity} label="Subscription" value={<StatusBadge status={sub?.status || "NONE"} />} testid="stat-sub-status" />
        <Stat icon={Zap} label="Remaining Analyses" testid="stat-remaining"
          value={!active ? "—" : isTime ? "UNLIMITED" : `${creditsLeft}`}
          sub={!active ? "" : isTime ? "Time-based access" : `of ${sub?.credits_total} analyses`} />
        <Stat icon={Clock} label={isTime ? "Time Remaining" : "Expiry"} testid="stat-countdown"
          value={!active ? "—" : isTime ? (countdown?.label || "—") : "On credits"}
          sub={isTime && active && sub?.expires_at ? new Date(sub.expires_at).toLocaleString() : ""} />
      </div>

      {active && sub?.activated_at && (
        <div className="vw-card p-6 grid gap-4 sm:grid-cols-3" data-testid="dashboard-sub-detail">
          <Detail icon={Calendar} label="Activated" value={new Date(sub.activated_at).toLocaleString()} />
          <Detail icon={Clock} label="Expiry" value={isTime && sub.expires_at ? new Date(sub.expires_at).toLocaleString() : "When credits run out"} />
          <Detail icon={CreditCard} label="Amount Paid" value={GHS(sub.amount || 0)} />
        </div>
      )}

      {/* recent predictions */}
      <div className="vw-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-white">Recent Predictions</h3>
          <button onClick={() => navigate("/history")} className="text-sm text-emerald-400 font-semibold inline-flex items-center gap-1" data-testid="dashboard-view-history">
            VIEW ALL HISTORY <ArrowRight size={15} />
          </button>
        </div>
        {(!data?.recent_predictions || data.recent_predictions.length === 0) ? (
          <p className="text-sm text-slate-500 py-6 text-center">No predictions yet. Run your first analysis!</p>
        ) : (
          <div className="space-y-3">
            {data.recent_predictions.map((p) => (
              <div key={p.id} className="rounded-xl border border-slate-800 p-4" data-testid={`recent-pred-${p.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-slate-500 font-mono">{new Date(p.created_at).toLocaleString()}</span>
                  <span className="text-xs text-slate-500">{p.plan_used}</span>
                </div>
                <div className="space-y-2">
                  {p.fixtures.map((f, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-slate-200 truncate">{f.home_team} <span className="text-slate-500">vs</span> {f.away_team}</span>
                      <PredictionPill prediction={f.prediction} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* quick links */}
      <div className="grid gap-4 sm:grid-cols-2">
        <QuickLink icon={Inbox} title="Inbox" desc={`${data?.unread_messages || 0} unread message(s)`} onClick={() => navigate("/inbox")} testid="dashboard-inbox-link" />
        <QuickLink icon={LifeBuoy} title="Support" desc="Need help? Contact our team." onClick={() => navigate("/support")} testid="dashboard-support-link" />
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="vw-card p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-slate-400 text-xs font-mono uppercase tracking-wider">
      <Icon size={15} className="text-emerald-400" /> {label}
    </div>
    <div className="mt-2 font-display text-xl font-bold text-white">{value}</div>
    {sub ? <div className="text-xs text-slate-500 mt-0.5">{sub}</div> : null}
  </div>
);

const Detail = ({ icon: Icon, label, value }) => (
  <div>
    <div className="flex items-center gap-2 text-slate-400 text-xs font-mono uppercase tracking-wider"><Icon size={14} /> {label}</div>
    <div className="mt-1 text-sm text-white">{value}</div>
  </div>
);

const QuickLink = ({ icon: Icon, title, desc, onClick, testid }) => (
  <button onClick={onClick} className="vw-card p-5 text-left flex items-center gap-4 hover:border-emerald-500/40 transition-colors" data-testid={testid}>
    <div className="h-11 w-11 grid place-items-center rounded-xl bg-emerald-500/10 text-emerald-400"><Icon size={20} /></div>
    <div className="flex-1"><div className="font-semibold text-white">{title}</div><div className="text-sm text-slate-400">{desc}</div></div>
    <ArrowRight size={18} className="text-slate-500" />
  </button>
);

const Skeleton = () => (
  <div className="space-y-5">
    <div className="h-8 w-60 rounded bg-slate-800 animate-pulse" />
    <div className="h-28 rounded-2xl bg-slate-800/60 animate-pulse" />
    <div className="grid gap-4 sm:grid-cols-4">{[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded-2xl bg-slate-800/60 animate-pulse" />)}</div>
  </div>
);
